import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  ClipboardList, Shield, BookOpen, Plus, Edit, Trash2, Loader2, Eye, ExternalLink, FileText,
} from 'lucide-react';
import { corporateAPI } from '../../api';
import { toast } from 'sonner';

const CorporateDocsTab = ({ corpCredentials, corpRequirements, corpResources, fetchData }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('credential');
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const openDialog = (type, item = null) => { setDialogType(type); setEditItem(item); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData(e.target);
      if (editItem) {
        const idKey = dialogType === 'resource' ? 'resource_id' : dialogType === 'credential' ? 'credential_id' : 'requirement_id';
        fd.append(idKey, editItem.id);
      }
      if (dialogType === 'credential') await corporateAPI.upsertCredential(fd);
      else if (dialogType === 'requirement') await corporateAPI.upsertRequirement(fd);
      else await corporateAPI.upsertResource(fd);
      toast.success(editItem ? 'Updated' : 'Created');
      setDialogOpen(false);
      fetchData();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <>
      {/* Reportorial Requirements */}
      <Card className="border-[#E2E6EA]">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><ClipboardList className="h-5 w-5 text-[#F58A26]" />Reportorial Requirements</CardTitle>
            <CardDescription>Filing deadlines and reminders (e.g., GIS, AFS)</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="border-[#F58A26] text-[#F58A26] hover:bg-[#F58A26]/10" onClick={async () => {
              try { const res = await corporateAPI.seedDefaults(); toast.success(`${res.data.count} SEC defaults seeded`); fetchData(); } catch { toast.error('Failed'); }
            }} data-testid="seed-defaults-btn"><ClipboardList className="h-4 w-4 mr-1" />Seed SEC Defaults</Button>
            <Button size="sm" onClick={() => openDialog('requirement')} className="bg-[#F58A26] hover:bg-[#D67520]" data-testid="add-requirement-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
          </div>
        </CardHeader>
        <CardContent>
          {corpRequirements.length === 0 ? (
            <p className="text-sm text-[#A8B1B9] text-center py-4">No reportorial requirements added yet.</p>
          ) : (
            <div className="space-y-2">
              {corpRequirements.map((req) => (
                <div key={req.id} className="flex items-center justify-between p-3 rounded-lg border border-[#E2E6EA]" data-testid={`requirement-${req.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#1A1D21]">{req.name}</p>
                    <p className="text-xs text-[#A8B1B9]">Due: {req.due_date} · {req.recurrence} {req.notes && `· ${req.notes}`}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={async () => { await corporateAPI.toggleRequirementPublish(req.id); fetchData(); }}
                      className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors cursor-pointer ${req.published ? 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100' : 'bg-gray-50 text-gray-500 border-gray-300 hover:bg-gray-100'}`}>
                      <span className={`inline-block h-2 w-2 rounded-full ${req.published ? 'bg-green-500' : 'bg-gray-400'}`} />
                      {req.published ? 'Published' : 'Unpublished'}
                    </button>
                    <Badge className={req.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>{req.status}</Badge>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openDialog('requirement', req)}><Edit className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={async () => { await corporateAPI.deleteRequirement(req.id); fetchData(); toast.success('Deleted'); }}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Corporate Credentials */}
      <Card className="border-[#E2E6EA]">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><Shield className="h-5 w-5 text-[#00A3A3]" />Corporate Credentials</CardTitle>
            <CardDescription>SEC Registration, Articles of Incorporation, BIR, LGU Permits</CardDescription>
          </div>
          <Button size="sm" onClick={() => openDialog('credential')} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="add-credential-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
        </CardHeader>
        <CardContent>
          {corpCredentials.length === 0 ? (
            <p className="text-sm text-[#A8B1B9] text-center py-4">No corporate credentials uploaded yet.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {corpCredentials.map((cred) => (
                <div key={cred.id} className="flex items-center justify-between p-3 rounded-lg border border-[#E2E6EA]" data-testid={`credential-${cred.id}`}>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FileText className="h-4 w-4 text-[#00A3A3] shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#1A1D21] truncate">{cred.name}</p>
                      {cred.file_name && <p className="text-[10px] text-[#A8B1B9] truncate">{cred.file_name}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={async () => { await corporateAPI.toggleCredentialPublish(cred.id); fetchData(); }}
                      className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors cursor-pointer ${cred.published ? 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100' : 'bg-gray-50 text-gray-500 border-gray-300 hover:bg-gray-100'}`}>
                      <span className={`inline-block h-2 w-2 rounded-full ${cred.published ? 'bg-green-500' : 'bg-gray-400'}`} />
                      {cred.published ? 'Published' : 'Unpublished'}
                    </button>
                    {cred.file_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${cred.file_url}`} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#00A3A3]"><Eye className="h-3 w-3" /></Button></a>}
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openDialog('credential', cred)}><Edit className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={async () => { await corporateAPI.deleteCredential(cred.id); fetchData(); toast.success('Deleted'); }}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resources */}
      <Card className="border-[#E2E6EA]">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><BookOpen className="h-5 w-5 text-[#6366f1]" />Resources</CardTitle>
            <CardDescription>Audit Reforms, Practical Guidance, and other References</CardDescription>
          </div>
          <Button size="sm" onClick={() => openDialog('resource')} className="bg-[#6366f1] hover:bg-[#4f46e5]" data-testid="add-resource-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
        </CardHeader>
        <CardContent>
          {corpResources.length === 0 ? (
            <p className="text-sm text-[#A8B1B9] text-center py-4">No resources added yet.</p>
          ) : (
            <div className="space-y-2">
              {corpResources.map((res) => (
                <div key={res.id} className="flex items-center justify-between p-3 rounded-lg border border-[#E2E6EA]" data-testid={`resource-${res.id}`}>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <BookOpen className="h-4 w-4 text-[#6366f1] shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#1A1D21] truncate">{res.title}</p>
                      <p className="text-[10px] text-[#A8B1B9] capitalize">{res.category} {res.file_name && `· ${res.file_name}`}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={async () => { await corporateAPI.toggleResourcePublish(res.id); fetchData(); }}
                      className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors cursor-pointer ${res.published ? 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100' : 'bg-gray-50 text-gray-500 border-gray-300 hover:bg-gray-100'}`}
                      data-testid={`toggle-publish-${res.id}`}>
                      <span className={`inline-block h-2 w-2 rounded-full ${res.published ? 'bg-green-500' : 'bg-gray-400'}`} />
                      {res.published ? 'Published' : 'Unpublished'}
                    </button>
                    {res.file_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${res.file_url}`} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#6366f1]"><Eye className="h-3 w-3" /></Button></a>}
                    {res.external_url && <a href={res.external_url} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#6366f1]"><ExternalLink className="h-3 w-3" /></Button></a>}
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openDialog('resource', res)}><Edit className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={async () => { await corporateAPI.deleteResource(res.id); fetchData(); toast.success('Deleted'); }}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit' : 'Add'} {dialogType === 'credential' ? 'Corporate Credential' : dialogType === 'requirement' ? 'Reportorial Requirement' : 'Resource'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {dialogType === 'credential' && (
              <>
                <div><Label>Document Name</Label><Input name="name" defaultValue={editItem?.name || ''} placeholder="e.g., SEC Registration" required /></div>
                <div><Label>Upload Document (PDF/Image)</Label><Input name="file" type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" /></div>
                {editItem?.file_name && <p className="text-xs text-[#A8B1B9]">Current: {editItem.file_name}</p>}
              </>
            )}
            {dialogType === 'requirement' && (
              <>
                <div><Label>Requirement Name</Label><Input name="name" defaultValue={editItem?.name || ''} placeholder="e.g., General Information Sheet (GIS)" required /></div>
                <div><Label>Due Date</Label><Input name="due_date" type="date" defaultValue={editItem?.due_date || ''} required /></div>
                <div>
                  <Label>Recurrence</Label>
                  <select name="recurrence" defaultValue={editItem?.recurrence || 'annual'} className="w-full border border-[#E2E6EA] rounded-md px-3 py-2 text-sm">
                    <option value="annual">Annual</option><option value="quarterly">Quarterly</option><option value="monthly">Monthly</option><option value="one-time">One-time</option>
                  </select>
                </div>
                <div><Label>Notes (optional)</Label><Input name="notes" defaultValue={editItem?.notes || ''} placeholder="Additional notes" /></div>
              </>
            )}
            {dialogType === 'resource' && (
              <>
                <div><Label>Title</Label><Input name="title" defaultValue={editItem?.title || ''} placeholder="e.g., Audit Reform Guidelines" required /></div>
                <div>
                  <Label>Category</Label>
                  <select name="category" defaultValue={editItem?.category || 'reference'} className="w-full border border-[#E2E6EA] rounded-md px-3 py-2 text-sm">
                    <option value="audit_reform">Audit Reform</option><option value="practical_guidance">Practical Guidance</option><option value="reference">Reference</option><option value="other">Other</option>
                  </select>
                </div>
                <div><Label>External URL (optional)</Label><Input name="external_url" defaultValue={editItem?.external_url || ''} placeholder="https://..." /></div>
                <div><Label>Or Upload File</Label><Input name="file" type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xlsx" /></div>
                {editItem?.file_name && <p className="text-xs text-[#A8B1B9]">Current: {editItem.file_name}</p>}
              </>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#00A3A3] hover:bg-[#008585]">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editItem ? 'Update' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CorporateDocsTab;
